import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, ChefHat, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const difficultyColor = { Easy: "text-emerald-400", Medium: "text-amber-400", Hard: "text-red-400" };

export default function RecipeDetailModal({ recipe, mushroomName, onClose }) {
  const { t } = useLang();
  if (!recipe) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={e => e.target === e.currentTarget && onClose()}
        data-testid="recipe-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92 }}
          className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-[var(--c-border)] shadow-2xl"
          style={{ backgroundColor: 'var(--c-surface)' }}
          data-testid="recipe-modal"
        >
          {/* Image */}
          <div className="relative h-48 overflow-hidden rounded-t-2xl">
            <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(22,32,26,1) 0%, transparent 60%)' }} />
            <button
              onClick={onClose} data-testid="close-recipe-modal"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-4 left-4">
              <p className="text-xs text-[var(--c-muted)]">{mushroomName}</p>
              <h2 className="font-serif text-xl font-bold text-white">{recipe.name}</h2>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Meta */}
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-sm text-[var(--c-muted)]"><Clock className="w-4 h-4" />{recipe.time}</span>
              <span className={`flex items-center gap-1.5 text-sm font-medium ${difficultyColor[recipe.difficulty] || 'text-[var(--c-muted)]'}`}>
                <ChefHat className="w-4 h-4" />{recipe.difficulty}
              </span>
            </div>

            {recipe.description && <p className="text-sm text-[var(--c-muted)] leading-relaxed">{recipe.description}</p>}

            {/* Ingredients */}
            {recipe.ingredients?.length > 0 && (
              <div>
                <h3 className="font-semibold text-[var(--c-text)] mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold text-white" style={{ backgroundColor: '#D97A46' }}>I</span>
                  {t('recipe.ingredients')}
                </h3>
                <ul className="space-y-2">
                  {recipe.ingredients.map((ing, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--c-muted)]" data-testid={`ingredient-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D97A46] flex-shrink-0 mt-1.5" />
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Steps */}
            {recipe.steps?.length > 0 && (
              <div>
                <h3 className="font-semibold text-[var(--c-text)] mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold text-white" style={{ backgroundColor: '#D97A46' }}>S</span>
                  {t('recipe.steps')}
                </h3>
                <ol className="space-y-3">
                  {recipe.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`step-${i}`}>
                      <span className="w-6 h-6 rounded-full border border-[#D97A46]/40 text-[#D97A46] text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold">{i + 1}</span>
                      <p className="text-sm text-[var(--c-muted)] leading-relaxed">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {!recipe.ingredients?.length && !recipe.steps?.length && (
              <p className="text-sm text-[var(--c-muted)] text-center py-4">{t('recipe.comingSoon')}</p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
